import {
  archiveFrames,
  brand,
  capabilities,
  cases,
  footerGroups,
  heroDisciplines,
  marqueeWords,
  navigation,
  processSteps,
  reelClips,
  statistics,
  timeline,
} from "./content.js";

const arrowIcon = `
  <span class="button__icon" aria-hidden="true"></span>
`;

const brandLockup = (modifier = "") => `
  <span class="brand-lockup ${modifier}">
    <span class="brand-symbol" aria-hidden="true">
      <i class="brand-symbol__ring"></i>
      <i class="brand-symbol__ring"></i>
      <i class="brand-symbol__core"></i>
    </span>
    <span class="brand-copy">
      <strong>${brand.shortName}</strong>
      <small>${brand.englishName}</small>
    </span>
  </span>
`;

const tags = (items, dark = false) => `
  <ul class="tag-list" aria-label="相关服务">
    ${items
      .map(
        (item) => `
          <li class="tag ${dark ? "tag--dark" : ""}">${item}</li>
        `,
      )
      .join("")}
  </ul>
`;

const desktopNavigation = (currentPage) => `
  <nav class="desktop-nav" aria-label="主要导航">
    ${navigation
      .slice(0, 4)
      .map(
        (item) => `
          <a
            class="desktop-nav__link ${item.page === currentPage ? "is-active" : ""}"
            href="${item.href}"
            data-nav-link="${item.id}"
            ${item.page === currentPage ? 'aria-current="page"' : ""}
          >
            ${item.label}
          </a>
        `,
      )
      .join("")}
  </nav>
`;

const mobileNavigation = (currentPage) => `
  <div class="mobile-menu" id="mobile-menu" aria-hidden="true" data-mobile-menu>
    <nav class="mobile-menu__nav" aria-label="移动端导航">
      ${navigation
        .map(
          (item) => `
            <a
              class="mobile-menu__link ${item.page === currentPage ? "is-active" : ""}"
              href="${item.href}"
              data-mobile-menu-link
              ${item.page === currentPage ? 'aria-current="page"' : ""}
            >
              <span>${item.index}</span>
              ${item.label}
            </a>
          `,
        )
        .join("")}
    </nav>
    <div class="mobile-menu__meta body-small">
      <span>${brand.location}</span>
      <a href="tel:${brand.phone}">${brand.phone}</a>
      <a href="mailto:${brand.email}">${brand.email}</a>
    </div>
  </div>
`;

const preloader = () => `
  <div class="preloader" data-preloader>
    <div class="preloader__inner">
      <p class="preloader__title">正在校准光场</p>
      <p class="preloader__value" data-loader-value>000</p>
      <div class="preloader__track" aria-hidden="true">
        <div class="preloader__bar"></div>
      </div>
      <div class="preloader__meta utility-caption">
        <span>SHANYING NEBULA / SYSTEM</span>
        <span>VISUAL FREQUENCY 24FPS</span>
      </div>
    </div>
  </div>
`;

const header = (currentPage) => `
  <header class="site-header" data-header>
    <a class="header-brand" href="index.html" aria-label="${brand.fullName}首页">
      ${brandLockup()}
    </a>
    <div class="header-nav">
      ${desktopNavigation(currentPage)}
    </div>
    <div class="header-actions">
      <a class="button button--ghost magnetic" href="contact.html" data-magnetic>
        <span>发起项目</span>
        ${arrowIcon}
      </a>
      <button
        class="menu-toggle"
        type="button"
        aria-controls="mobile-menu"
        aria-expanded="false"
        aria-label="打开菜单"
        data-menu-toggle
      >
        <span class="menu-toggle__lines" aria-hidden="true">
          <i class="menu-toggle__line"></i>
          <i class="menu-toggle__line"></i>
        </span>
      </button>
    </div>
  </header>
  ${mobileNavigation(currentPage)}
`;

const hero = () => `
  <section class="hero" id="top" aria-labelledby="hero-title" data-section="top">
    <div class="hero__stage" aria-hidden="true" data-hero-stage>
      <img
        class="hero__media"
        src="image/hero-hongkong.webp"
        alt=""
        decoding="async"
        fetchpriority="high"
      >
      <div class="hero__aperture" data-hero-aperture>
        <span class="hero__aperture-blade hero__aperture-blade--top" data-aperture-blade></span>
        <span class="hero__aperture-blade hero__aperture-blade--right" data-aperture-blade></span>
        <span class="hero__aperture-blade hero__aperture-blade--bottom" data-aperture-blade></span>
        <span class="hero__aperture-blade hero__aperture-blade--left" data-aperture-blade></span>
        <span class="hero__aperture-frame"></span>
        <span class="hero__aperture-label">SHANYING / LIGHT GATE 01</span>
      </div>
    </div>
    <div
      class="hero__particle-field"
      data-nebula-field
      aria-hidden="true"
    ></div>
    <div class="hero__content">
      <div class="hero__topline">
        <p class="hero__availability">正在接受品牌影像与传播项目</p>
        <p class="hero__clock" data-clock>CN / 00:00:00</p>
      </div>
      <div class="hero__center">
        <h1 class="hero__title display-hero" id="hero-title">
          <span class="hero__title-line split-line">
            <span class="split-line__inner">让每一次</span>
          </span>
          <span class="hero__title-line split-line">
            <span class="split-line__inner text-chrome">闪耀</span>
          </span>
          <span class="hero__title-line split-line hero__title-line--accent">
            <span class="split-line__inner">
              成为引力
              <i class="hero__title-mark" aria-hidden="true"></i>
            </span>
          </span>
        </h1>
        <p class="hero__subtitle body-medium">
          我们把策略、创意、影像与传播编入同一条轨道，
          为品牌创造能被感知、记住并持续扩散的内容。
        </p>
      </div>
      <div class="hero__bottom">
        <div class="hero__actions">
          <a class="button button--primary magnetic" href="work.html" data-magnetic>
            <span>进入作品场</span>
            ${arrowIcon}
          </a>
          <a class="link-arrow" href="about.html">
            了解闪影星云
            <span aria-hidden="true">↘</span>
          </a>
        </div>
        <a class="hero__scroll" href="#about" aria-label="向下浏览">
          <span>Scroll to explore</span>
          <i class="hero__scroll-line" aria-hidden="true"></i>
        </a>
        <div class="hero__discipline" aria-label="核心服务">
          ${heroDisciplines.map((item) => `<span>${item}</span>`).join("")}
        </div>
      </div>
    </div>
  </section>
`;

const manifesto = () => `
  <section
    class="manifesto"
    id="about"
    aria-labelledby="manifesto-title"
    data-section="about"
  >
    <div class="section-shell">
      <div class="manifesto__header" data-reveal>
        <p class="section-kicker section-kicker--dark">我们的主张 / Manifesto</p>
        <p class="manifesto__edition">FIELD NOTE · 001 / 2026</p>
      </div>
      <h2 class="manifesto__statement display-section" id="manifesto-title">
        <span class="manifesto__statement-line" data-scrub-line>
          不追逐转瞬即逝的热闹，
        </span>
        <span
          class="manifesto__statement-line"
          data-scrub-line
        >
          我们创造值得留下的<em>记忆</em>，
        </span>
        <span
          class="manifesto__statement-line"
          data-scrub-line
        >
          以及继续向外生长的影响力。
        </span>
      </h2>
      <div class="manifesto__detail">
        <p class="body-medium" data-reveal>
          闪影星云文化传媒，以内容为引力，以影像为光。
          我们从品牌真实的价值出发，把一个想法发展成完整、清晰、可抵达的表达。
        </p>
        <p class="body-medium" data-reveal style="--reveal-delay: 120ms">
          每个项目都应该拥有自己的语气、节奏与轨迹，
          而不是另一份换了标志的标准答案。我们相信，高级感来自准确，而不是堆砌。
        </p>
      </div>
      <p class="manifesto__signature">SHANYING NEBULA / CULTURE MEDIA</p>
    </div>
  </section>
`;

const lightStudy = () => `
  <section class="light-study" id="light-study" aria-labelledby="light-study-title">
    <div class="section-shell light-study__header">
      <div>
        <p class="section-kicker section-kicker--electric" data-reveal>
          光线研究 / Light study
        </p>
        <p class="light-study__edition">ONE LANDMARK · THREE EXPOSURES</p>
      </div>
      <h2 class="display-section" id="light-study-title" data-reveal>
        同一座塔，<br>三种不同的呼吸。
      </h2>
      <p class="body-medium" data-reveal>
        蓝调、亮灯与日落不是三张重复的照片，
        而是同一空间在不同光线下呈现出的三种叙事密度。
      </p>
    </div>
    <div
      class="light-study__particles"
      data-nebula-field
      data-particle-profile="pagoda"
      aria-hidden="true"
    ></div>
    <div class="light-study__stage" data-light-study>
      <figure
        class="light-frame light-frame--glow is-selected"
        tabindex="0"
        role="button"
        aria-label="查看蓝调亮灯照片"
        aria-pressed="true"
        data-light-frame
        data-depth-surface
        data-reveal="scale"
      >
        <img
          src="image/pagoda-blue-glow.webp"
          alt="深蓝天空与岩石溪流之间亮起金色灯光的塔楼"
          loading="lazy"
          decoding="async"
        >
        <figcaption>
          <span>EXPOSURE / A</span><strong>蓝调亮灯</strong><small>COBALT</small>
        </figcaption>
      </figure>
      <figure
        class="light-frame light-frame--soft"
        tabindex="0"
        role="button"
        aria-label="查看暮色将至照片"
        aria-pressed="false"
        data-light-frame
        data-depth-surface
        data-reveal="scale"
      >
        <img
          src="image/pagoda-blue-soft.webp"
          alt="柔和蓝调天空下，岩石与流水之间的塔楼"
          loading="lazy"
          decoding="async"
        >
        <figcaption>
          <span>EXPOSURE / B</span><strong>暮色将至</strong><small>BLUE HOUR</small>
        </figcaption>
      </figure>
      <figure
        class="light-frame light-frame--sunset"
        tabindex="0"
        role="button"
        aria-label="查看日落余晖照片"
        aria-pressed="false"
        data-light-frame
        data-depth-surface
        data-reveal="scale"
      >
        <img
          src="image/pagoda-sunset.webp"
          alt="金色日落云层与岩石溪流之间的塔楼"
          loading="lazy"
          decoding="async"
        >
        <figcaption>
          <span>EXPOSURE / C</span><strong>日落余晖</strong><small>AMBER</small>
        </figcaption>
      </figure>
    </div>
    <p class="light-study__note">交互式三联画 · 点击选择曝光，触碰画面唤醒空间粒子</p>
  </section>
`;

const caseCards = () =>
  cases
    .map(
      (item) => `
        <article class="case-card" data-case-card data-surface-light>
          <div class="case-card__visual">
            <figure class="case-card__media">
              <img
                class="case-card__image"
                src="${item.media}"
                alt="${item.alt}"
                loading="lazy"
                decoding="async"
              >
              <figcaption class="case-card__media-caption">
                <span>${item.mediaLabel}</span>
                <span>${item.ratio}</span>
              </figcaption>
            </figure>
          </div>
          <div class="case-card__body">
            <div>
              <p class="case-card__number">${item.number} · ${item.category}</p>
              <h3 class="case-card__title display-card">${item.title}</h3>
              <p class="case-card__description body-medium">${item.description}</p>
            </div>
            <div class="case-card__meta">
              ${tags(item.tags)}
              <div class="case-card__result">
                <strong>${item.result}</strong>
                <span>${item.resultLabel}</span>
              </div>
            </div>
          </div>
        </article>
      `,
    )
    .join("");

const casesSection = () => `
  <section
    class="cases"
    id="work"
    aria-labelledby="cases-title"
    data-section="work"
    data-cases-scene
  >
    <div class="cases__header">
      <p class="section-kicker section-kicker--electric" data-reveal>
        项目方向 / Selected work
      </p>
      <h2 class="cases__title display-section" id="cases-title" data-reveal>
        让作品自己发光
      </h2>
      <p class="cases__intro body-small" data-reveal>
        从现有成片中选取不同题材的真实画面，
        让影像本身承担叙事，而不是只做装饰。
      </p>
    </div>
    <div class="cases__track-wrap" data-case-viewport>
      <div class="cases__track" data-case-track>
        ${caseCards()}
      </div>
    </div>
    <div class="cases__controls">
      <div class="cases__progress" aria-hidden="true">
        <div class="cases__progress-bar"></div>
      </div>
      <div class="cases__buttons">
        <button
          class="circle-button cases__button--prev"
          type="button"
          aria-label="上一个项目"
          data-case-prev
        >
          ${arrowIcon}
        </button>
        <button
          class="circle-button"
          type="button"
          aria-label="下一个项目"
          data-case-next
        >
          ${arrowIcon}
        </button>
      </div>
    </div>
  </section>
`;

const archiveShots = () =>
  archiveFrames
    .map(
      (frame, index) => `
        <figure
          class="archive-shot ${frame.layout}"
          tabindex="0"
          role="button"
          aria-label="放大查看${frame.title}"
          data-archive-index="${index}"
          data-archive-src="${frame.src}"
          data-archive-alt="${frame.alt}"
          data-archive-title="${frame.title}"
          data-archive-note="${frame.note}"
          data-archive-shot
          data-depth-surface
          data-reveal="scale"
        >
          <div class="archive-shot__media" data-archive-media>
            <img
              class="archive-shot__image"
              src="${frame.src}"
              alt="${frame.alt}"
              loading="lazy"
              decoding="async"
            >
          </div>
          ${
            frame.particleProfile
              ? `<div
                  class="archive-shot__particles"
                  data-nebula-field
                  data-particle-profile="${frame.particleProfile}"
                  aria-hidden="true"
                ></div>`
              : ""
          }
          <figcaption class="archive-shot__caption">
            <span>${frame.index}</span>
            <strong>${frame.title}</strong>
            <small>${frame.note}</small>
          </figcaption>
        </figure>
      `,
    )
    .join("");

const archive = () => `
  <section class="archive" aria-labelledby="archive-title">
    <div class="section-shell archive__header">
      <div>
        <p class="section-kicker section-kicker--electric" data-reveal>
          影像档案 / Field archive
        </p>
        <p class="archive__edition">09 FRAMES · REAL-WORLD OBSERVATION</p>
      </div>
      <h2 class="archive__title display-section" id="archive-title" data-reveal>
        真实世界，<br>才是我们的素材库。
      </h2>
      <p class="archive__intro body-medium" data-reveal>
        从校园、夜色、飞行、自然、餐桌到活动现场，镜头先抵达真实，
        再把光线、情绪与发生过的瞬间编进同一段叙事。
      </p>
    </div>
    <div class="archive__grid">
      ${archiveShots()}
    </div>
    <div class="archive__film-edge" aria-hidden="true">
      <div class="archive__film-track">
        <span>FRAME 01</span><span>LIGHT</span><span>FRAME 03</span>
        <span>MOTION</span><span>FRAME 05</span><span>FIELD</span>
        <span>FRAME 07</span><span>MEMORY</span><span>FRAME 09</span>
        <span>FRAME 01</span><span>LIGHT</span><span>FRAME 03</span>
        <span>MOTION</span><span>FRAME 05</span><span>FIELD</span>
        <span>FRAME 07</span><span>MEMORY</span><span>FRAME 09</span>
      </div>
    </div>
  </section>
`;

const capabilityNodes = () =>
  capabilities
    .map(
      (item, index) => `
        <button
          class="orbit-node ${index === 0 ? "is-active" : ""}"
          type="button"
          data-capability-node
          data-index="${index}"
          aria-pressed="${index === 0 ? "true" : "false"}"
        >
          ${item.short}
        </button>
      `,
    )
    .join("");

const capabilitiesSection = () => {
  const first = capabilities[0];

  return `
    <section
      class="capabilities"
      id="capabilities"
      aria-labelledby="capabilities-title"
      data-section="capabilities"
    >
      <div class="section-shell capabilities__layout">
        <div class="capabilities__copy">
          <p class="section-kicker section-kicker--electric" data-reveal>
            服务能力 / Capabilities
          </p>
          <h2
            class="capabilities__title display-section"
            id="capabilities-title"
            data-reveal
          >
            一套完整的<br>内容引力系统
          </h2>
          <p class="capabilities__lead body-medium" data-reveal>
            从为什么说，到说什么、怎么拍、在哪里被看见。
            我们用同一条创意主线串联全部环节。
          </p>
          <div class="capabilities__active" data-capability-panel data-reveal>
            <p class="capabilities__active-index" data-capability-index>${first.index}</p>
            <h3 class="capabilities__active-title" data-capability-title>
              ${first.title}
            </h3>
            <p class="capabilities__active-text body-medium" data-capability-text>
              ${first.description}
            </p>
            <div class="capabilities__active-tags" data-capability-tags>
              ${tags(first.tags)}
            </div>
          </div>
        </div>
        <div class="capabilities__diagram" data-reveal="scale">
          <div class="orbit-diagram">
            <i class="orbit-diagram__ring orbit-diagram__ring--outer" aria-hidden="true"></i>
            <i class="orbit-diagram__ring orbit-diagram__ring--middle" aria-hidden="true"></i>
            <i class="orbit-diagram__ring orbit-diagram__ring--inner" aria-hidden="true"></i>
            <i class="orbit-diagram__axis" aria-hidden="true"></i>
            <i class="orbit-diagram__axis" aria-hidden="true"></i>
            <div class="orbit-diagram__core">
              <strong>内容<br>引力场</strong>
            </div>
            ${capabilityNodes()}
          </div>
        </div>
      </div>
      <p class="capabilities__footnote">FIVE CONNECTED DISCIPLINES / ONE CREATIVE DIRECTION</p>
    </section>
  `;
};

const reelSelector = () =>
  reelClips
    .map(
      (clip, index) => `
        <button
          class="reel-clip ${index === 0 ? "is-active" : ""}"
          type="button"
          aria-label="切换到${clip.title}"
          aria-pressed="${index === 0 ? "true" : "false"}"
          data-reel-clip
          data-reel-src="${clip.src}"
          data-reel-poster="${clip.poster}"
          data-reel-index="${clip.index}"
          data-reel-title="${clip.title}"
          data-reel-note="${clip.note}"
          data-reel-duration="${clip.duration}"
        >
          <span class="reel-clip__visual">
            <img src="${clip.poster}" alt="" loading="lazy" decoding="async">
            <i aria-hidden="true"></i>
          </span>
          <span class="reel-clip__copy">
            <small>${clip.index}</small>
            <strong>${clip.title}</strong>
            <em>${clip.duration}</em>
          </span>
        </button>
      `,
    )
    .join("");

const showreel = () => {
  const firstClip = reelClips[0];

  return `
    <section class="showreel" id="showreel" aria-labelledby="showreel-title">
      <div class="section-shell">
        <div class="showreel__header">
          <p class="section-kicker section-kicker--dark" data-reveal>
            影像母带 / Motion archive
          </p>
          <h2 class="showreel__title display-section" id="showreel-title" data-reveal>
            四段真实光线，<br>组成一条动态母带。
          </h2>
        </div>
        <div class="showreel__stage" data-cinematic-mask>
          <video
            class="showreel__video"
            src="${firstClip.src}"
            poster="${firstClip.poster}"
            preload="metadata"
            muted
            loop
            playsinline
            data-showreel-video
          ></video>
          <span class="showreel__shade" aria-hidden="true"></span>
          <button
            class="showreel__play"
            type="button"
            aria-label="播放${firstClip.title}"
            aria-pressed="false"
            data-showreel-button
          >
            <span class="showreel__play-icon" aria-hidden="true"></span>
          </button>
          <p class="showreel__active-title" data-showreel-active-title>
            ${firstClip.title}
          </p>
          <div
            class="showreel__timeline"
            role="slider"
            tabindex="0"
            aria-label="影片播放进度"
            aria-valuemin="0"
            aria-valuemax="100"
            aria-valuenow="0"
            data-video-timeline
          >
            <span class="showreel__timeline-fill" data-video-timeline-fill></span>
            <i class="showreel__timeline-handle" aria-hidden="true"></i>
          </div>
        </div>
        <div class="showreel__caption body-small">
          <strong data-showreel-label>${firstClip.index} · ${firstClip.title}</strong>
          <span data-showreel-detail>${firstClip.note}</span>
          <span data-showreel-time>00:00 / ${firstClip.duration}</span>
        </div>
        <div class="showreel__clips" data-reveal>
          ${reelSelector()}
        </div>
      </div>
    </section>
  `;
};

const processCards = () =>
  processSteps
    .map(
      (step, index) => `
        <article
          class="process-card"
          data-reveal
          style="--reveal-delay: ${index * 90}ms"
        >
          <p class="process-card__number">${step.number}</p>
          <div class="process-card__body">
            <h3 class="process-card__title">${step.title}</h3>
            <p class="process-card__description">${step.description}</p>
          </div>
        </article>
      `,
    )
    .join("");

const method = () => `
  <section
    class="method"
    id="method"
    aria-labelledby="method-title"
    data-section="method"
  >
    <div class="section-shell">
      <div class="method__header">
        <p class="section-kicker section-kicker--dark" data-reveal>
          合作方式 / Method
        </p>
        <h2 class="method__title display-section" id="method-title" data-reveal>
          保持简单，<br>把复杂留给我们。
        </h2>
      </div>
      <div class="method__grid">
        ${processCards()}
      </div>
      <div class="method__principle">
        <h3 class="method__principle-title" data-reveal>
          同一个目标，<br>一条透明的轨道。
        </h3>
        <p class="method__principle-text body-medium" data-reveal>
          我们在项目开始时就对齐目标、范围、预算与节奏；
          在关键节点给出明确判断，并让每次反馈都推动作品变得更好。
        </p>
        <div class="method__principle-mark" data-reveal="scale">
          ONE<br>ORBIT
        </div>
      </div>
    </div>
  </section>
`;

const timelineItems = () =>
  timeline
    .map(
      (item, index) => `
        <article class="timeline-item" data-reveal="right" data-chapter-item>
          <p class="timeline-item__year">${item.year}</p>
          <div>
            <h3 class="timeline-item__title heading-medium">${item.title}</h3>
            <p class="timeline-item__description body-medium">${item.description}</p>
          </div>
        </article>
      `,
    )
    .join("");

const statLine = () => `
  <div class="stat-line">
    ${statistics
      .map(
        (item) => `
          <div class="stat-line__item">
            <strong class="stat-line__value" data-counter="${item.value}">
              ${item.value}${item.suffix}
            </strong>
            <span class="stat-line__label">${item.label}</span>
          </div>
        `,
      )
      .join("")}
  </div>
`;

const timelineSection = () => `
  <section class="timeline" aria-labelledby="timeline-title">
    <div class="section-shell timeline__layout">
      <div class="timeline__intro">
        <p class="section-kicker section-kicker--electric" data-reveal>
          创作原则 / Chapters
        </p>
        <h2 class="timeline__title display-section" id="timeline-title" data-reveal>
          从一道闪光，<br>到持续引力。
        </h2>
        <p class="timeline__lead body-medium" data-reveal>
          好内容不是一次性的烟火，而是品牌与人建立长期关系的开始。
        </p>
        <div class="timeline__closing" data-reveal>
          <strong>MAKE IT RESONATE.</strong>
          <p>让每次表达都有清晰目的、独特语气与真实回声。</p>
        </div>
      </div>
      <div class="timeline__list">
        ${timelineItems()}
      </div>
    </div>
    <div class="section-shell">
      ${statLine()}
    </div>
  </section>
`;

const marqueeContent = () => {
  const sequence = marqueeWords
    .map(
      (word) => `
        <span class="marquee__item">
          ${word}
          <i class="marquee__separator" aria-hidden="true"></i>
        </span>
      `,
    )
    .join("");

  return `${sequence}${sequence}`;
};

const closingMarquee = () => `
  <div class="closing-marquee marquee" aria-hidden="true">
    <div class="marquee__track">
      ${marqueeContent()}
    </div>
  </div>
`;

const contact = () => `
  <section
    class="contact"
    id="contact"
    aria-labelledby="contact-title"
    data-section="contact"
  >
    <div class="section-shell contact__content">
      <p class="section-kicker section-kicker--dark" data-reveal>
        下一束光 / Start a project
      </p>
      <h2 class="contact__title display-section" id="contact-title">
        <span class="contact__title-line" data-scrub-line>有一个值得</span>
        <span
          class="contact__title-line"
          data-scrub-line
        >
          被看见的<em>想法？</em>
        </span>
      </h2>
      <div class="contact__row">
        <p class="contact__copy body-large" data-reveal>
          告诉我们项目目标、时间与大致需求。
          我们会带着明确判断和可执行方向与你联系。
        </p>
        <div class="contact__actions" data-reveal>
          <a class="button button--dark magnetic" href="mailto:${brand.email}" data-magnetic>
            <span>发送项目简报</span>
            ${arrowIcon}
          </a>
          <a class="link-arrow" href="tel:${brand.phone}">
            ${brand.phone}
            <span aria-hidden="true">↗</span>
          </a>
          <a class="link-arrow" href="#top">
            返回顶部
            <span aria-hidden="true">↑</span>
          </a>
        </div>
      </div>
      <p class="contact__note">${brand.email} · ${brand.phone}</p>
    </div>
    <i class="contact__spark" aria-hidden="true"></i>
    <p class="contact__orbit-label">SHANYING NEBULA / OPEN FOR COLLABORATION</p>
  </section>
`;

const footerLink = (link) => {
  const href = link.href ?? "index.html";

  return `
    <a class="site-footer__nav-link" href="${href}">${link.label}</a>
  `;
};

const footer = () => `
  <footer class="site-footer">
    <div class="site-footer__top">
      <div class="site-footer__brand">
        ${brandLockup()}
        <p class="site-footer__statement body-medium">
          ${brand.statement}<br>
          为品牌创造可感知、可记忆、可持续传播的内容。
        </p>
      </div>
      <div class="site-footer__nav">
        ${footerGroups
          .map(
            (group) => `
              <div class="site-footer__nav-group">
                <p class="site-footer__nav-title">${group.title}</p>
                ${group.links.map(footerLink).join("")}
              </div>
            `,
          )
          .join("")}
      </div>
      <div class="site-footer__contact">
        <p class="utility-caption">PROJECT INQUIRIES</p>
        <a class="site-footer__email" href="mailto:${brand.email}">
          ${brand.email}
        </a>
        <a class="site-footer__phone body-small" href="tel:${brand.phone}">
          ${brand.phone}
        </a>
        <p class="body-small">${brand.location}</p>
      </div>
    </div>
    <div class="site-footer__bottom">
      <p>© <span data-year></span> ${brand.fullName}</p>
      <p>STRATEGY · FILM · EXPERIENCE · CAMPAIGN</p>
    </div>
  </footer>
`;

const pageHero = ({
  page,
  code,
  kicker,
  title,
  accent,
  copy,
  image,
  imageLabel,
  particleProfile = "nebula",
}) => `
  <section
    class="page-hero page-hero--${page}"
    id="top"
    aria-labelledby="page-hero-title"
    data-section="${page === "services" ? "capabilities" : page}"
  >
    <div
      class="page-hero__particle-field"
      data-nebula-field
      data-particle-profile="${particleProfile}"
      aria-hidden="true"
    ></div>
    <figure class="page-hero__visual" data-depth-surface data-reveal="scale">
      <img src="${image}" alt="" fetchpriority="high" decoding="async">
      <figcaption>
        <span>${imageLabel}</span>
        <span>SHANYING / ORIGINAL FRAME</span>
      </figcaption>
    </figure>
    <div class="page-hero__scan" aria-hidden="true"></div>
    <div class="section-shell page-hero__content">
      <div class="page-hero__meta">
        <p class="section-kicker section-kicker--electric">${kicker}</p>
        <p class="page-hero__code">${code} / 05</p>
      </div>
      <h1 class="page-hero__title display-hero" id="page-hero-title">
        <span class="page-hero__title-line split-line">
          <span class="split-line__inner">${title}</span>
        </span>
        <span class="page-hero__title-line split-line">
          <span class="split-line__inner text-chrome">${accent}</span>
        </span>
      </h1>
      <div class="page-hero__footer">
        <p class="page-hero__copy body-large">${copy}</p>
        <a class="page-hero__scroll" href="#page-start">
          <span>进入本页</span>
          <i aria-hidden="true"></i>
        </a>
      </div>
    </div>
    <div class="page-hero__film-data" aria-hidden="true">
      <span>RAW / 10-BIT</span><span>24 FPS</span><span>LIGHT / FIELD</span>
    </div>
  </section>
`;

const studioProfile = () => `
  <section class="studio-profile" id="page-start" aria-labelledby="studio-profile-title">
    <div class="section-shell studio-profile__layout">
      <div class="studio-profile__copy">
        <p class="section-kicker section-kicker--dark" data-reveal>
          工作室侧写 / Studio profile
        </p>
        <h2 class="display-section" id="studio-profile-title" data-reveal>
          我们不先选择风格，<br>我们先找到问题的真相。
        </h2>
        <div class="studio-profile__text body-medium" data-reveal>
          <p>
            闪影星云文化传媒把策略、导演思维、视觉设计与制作执行放在同一张桌上。
            我们从品牌真正需要解决的事情出发，再决定镜头应该如何运动、文字应该如何出现。
          </p>
          <p>
            因此，每个项目都拥有不同的形状，却共享同一种标准：表达准确、画面耐看、传播有效。
          </p>
        </div>
      </div>
      <div class="studio-profile__frames">
        <figure class="studio-frame studio-frame--primary" data-reveal="scale" data-parallax="0.045">
          <img src="image/pagoda-blue-soft.webp" alt="蓝调天空与岩石流水之间的塔楼" loading="lazy">
          <figcaption>文化观察 / FRAME A</figcaption>
        </figure>
        <figure class="studio-frame studio-frame--secondary" data-reveal="scale" data-parallax="-0.035">
          <img src="image/archive-live.webp" alt="灯光照亮的活动演出现场" loading="lazy">
          <figcaption>现场能量 / FRAME B</figcaption>
        </figure>
      </div>
    </div>
  </section>
`;

const projectTiles = () => `
  <section class="project-index" id="page-start" aria-labelledby="project-index-title">
    <div class="section-shell project-index__header">
      <p class="section-kicker section-kicker--electric" data-reveal>
        项目索引 / Project index
      </p>
      <h2 class="display-section" id="project-index-title" data-reveal>
        四种现场，<br>四种叙事方法。
      </h2>
      <p class="body-medium" data-reveal>
        这里展示的是我们的创作方向：从品牌影像到活动记录，
        每一次都从真实素材里寻找独有的节奏。
      </p>
    </div>
    <div class="section-shell project-index__grid">
      ${cases
        .map(
          (item, index) => `
            <article class="project-tile project-tile--${index + 1}" data-reveal="scale" data-surface-light data-depth-surface>
              <figure class="project-tile__media">
                <img src="${item.media}" alt="${item.alt}" loading="lazy" decoding="async">
                <figcaption><span>${item.number}</span><span>${item.ratio}</span></figcaption>
              </figure>
              <div class="project-tile__body">
                <p>${item.category}</p>
                <h3>${item.title}</h3>
                ${tags(item.tags)}
              </div>
            </article>
          `,
        )
        .join("")}
    </div>
  </section>
`;

const serviceLedger = () => `
  <section class="service-ledger" id="page-start" aria-labelledby="service-ledger-title">
    <div class="section-shell">
      <div class="service-ledger__header">
        <p class="section-kicker section-kicker--dark" data-reveal>
          制作清单 / Production ledger
        </p>
        <h2 class="display-section" id="service-ledger-title" data-reveal>
          从一句为什么，<br>到作品真正抵达。
        </h2>
      </div>
      <div class="service-ledger__list">
        ${capabilities
          .map(
            (item) => `
              <article class="service-row" data-reveal data-surface-light>
                <p class="service-row__index">${item.index}</p>
                <h3>${item.title}</h3>
                <p class="service-row__description">${item.description}</p>
                <div>${tags(item.tags, true)}</div>
              </article>
            `,
          )
          .join("")}
      </div>
    </div>
  </section>
`;

const pageOutro = () => `
  <section class="page-outro" aria-labelledby="page-outro-title">
    <div
      class="page-outro__particle-field"
      data-nebula-field
      data-particle-profile="ambient"
      aria-hidden="true"
    ></div>
    <div class="section-shell page-outro__layout">
      <p class="section-kicker section-kicker--electric" data-reveal>
        下一束光 / Next frame
      </p>
      <h2 class="display-section" id="page-outro-title" data-reveal>
        你的下一次表达，<br>可以从这里开始。
      </h2>
      <div class="page-outro__actions" data-reveal>
        <a class="button button--primary magnetic" href="contact.html" data-magnetic>
          <span>发起项目</span>${arrowIcon}
        </a>
        <a class="link-arrow" href="tel:${brand.phone}">
          ${brand.phone}<span aria-hidden="true">↗</span>
        </a>
        <a class="link-arrow" href="mailto:${brand.email}">
          ${brand.email}<span aria-hidden="true">↗</span>
        </a>
      </div>
    </div>
  </section>
`;

const contactDesk = () => `
  <section class="contact-desk" id="page-start" aria-labelledby="contact-desk-title">
    <div class="section-shell contact-desk__layout">
      <div class="contact-desk__intro">
        <p class="section-kicker section-kicker--dark" data-reveal>
          项目联络 / Project inquiries
        </p>
        <h2 class="display-section" id="contact-desk-title" data-reveal>
          先说目标，<br>再一起决定怎么拍。
        </h2>
        <p class="body-medium" data-reveal>
          可以告诉我们项目类型、期望上线时间、预算范围，以及你最希望观众记住的一件事。
          信息不必完整，我们会协助梳理。
        </p>
      </div>
      <div class="contact-desk__channels">
        <a class="contact-channel" href="tel:${brand.phone}" data-reveal data-surface-light>
          <span>电话 / PHONE</span>
          <strong>${brand.phone}</strong>
          <i aria-hidden="true">↗</i>
        </a>
        <a class="contact-channel" href="mailto:${brand.email}" data-reveal data-surface-light>
          <span>邮箱 / EMAIL</span>
          <strong>${brand.email}</strong>
          <i aria-hidden="true">↗</i>
        </a>
      </div>
      <div class="contact-brief" data-reveal>
        <p class="utility-caption">开始前可以准备 / BEFORE WE TALK</p>
        <ol>
          <li><span>01</span>你想解决的核心问题</li>
          <li><span>02</span>主要受众与发布场景</li>
          <li><span>03</span>期望完成或上线的时间</li>
          <li><span>04</span>预算范围与现有素材</li>
        </ol>
      </div>
    </div>
  </section>
`;

const homePage = () => `
  ${hero()}
  ${manifesto()}
  ${lightStudy()}
  ${casesSection()}
  ${archive()}
  ${capabilitiesSection()}
  ${showreel()}
  ${method()}
  ${timelineSection()}
  ${closingMarquee()}
  ${contact()}
`;

const aboutPage = () => `
  ${pageHero({
    page: "about",
    code: "01",
    kicker: "我们是谁 / Studio",
    title: "观察真实，",
    accent: "再创造回声。",
    copy: "我们是一支以策略为起点、以影像为主要语言的文化传媒团队。高级感不是装饰，而是每一次判断都足够准确。",
    image: "image/pagoda-blue-glow.webp",
    imageLabel: "CULTURE / OBSERVATION",
  })}
  ${studioProfile()}
  ${manifesto()}
  ${timelineSection()}
  ${pageOutro()}
`;

const workPage = () => `
  ${pageHero({
    page: "work",
    code: "02",
    kicker: "代表作品 / Selected work",
    title: "把现场，",
    accent: "变成记忆。",
    copy: "品牌、城市、自然与人群从不共享同一种情绪。我们为每个项目寻找自己的光线、景别与速度。",
    image: "image/archive-flight-night.webp",
    imageLabel: "FLIGHT / NIGHT FIELD",
  })}
  ${projectTiles()}
  ${archive()}
  ${showreel()}
  ${pageOutro()}
`;

const servicesPage = () => `
  ${pageHero({
    page: "services",
    code: "03",
    kicker: "服务能力 / Capabilities",
    title: "一条主线，",
    accent: "贯穿全部触点。",
    copy: "从策略定义到现场执行，再到上线传播，我们让所有环节围绕同一个清晰目标工作。",
    image: "image/archive-clouds.webp",
    imageLabel: "CLOUD / DIRECTION",
  })}
  ${serviceLedger()}
  ${capabilitiesSection()}
  ${method()}
  ${pageOutro()}
`;

const contactPage = () => `
  ${pageHero({
    page: "contact",
    code: "04",
    kicker: "联系合作 / Contact",
    title: "下一束光，",
    accent: "从一次对话开始。",
    copy: "如果你正在准备一支影片、一场活动或一次品牌表达，请把目标告诉我们。我们会从问题本身开始回应。",
    image: "image/archive-street.webp",
    imageLabel: "NPU CAMPUS / AUTUMN LIGHT",
    particleProfile: "campus",
  })}
  ${contactDesk()}
  ${closingMarquee()}
`;

const renderPage = (currentPage) => {
  const pages = {
    home: homePage,
    about: aboutPage,
    work: workPage,
    services: servicesPage,
    contact: contactPage,
  };

  return (pages[currentPage] ?? pages.home)();
};

const mediaInspector = () => `
  <dialog
    class="media-inspector"
    data-media-inspector
    aria-labelledby="media-inspector-title"
  >
    <div class="media-inspector__backdrop" data-inspector-close></div>
    <div class="media-inspector__panel">
      <div class="media-inspector__visual">
        <img src="" alt="" data-inspector-image>
        <span class="media-inspector__scan" aria-hidden="true"></span>
      </div>
      <div class="media-inspector__meta">
        <p class="utility-caption" data-inspector-index>FRAME / 01</p>
        <h2 id="media-inspector-title" data-inspector-title>影像档案</h2>
        <p data-inspector-note>真实世界观察</p>
      </div>
      <button
        class="media-inspector__close"
        type="button"
        aria-label="关闭影像查看器"
        data-inspector-close
      >
        <span aria-hidden="true"></span>
      </button>
      <div class="media-inspector__controls">
        <button type="button" aria-label="上一张照片" data-inspector-prev>←</button>
        <p><span data-inspector-current>01</span> / <span data-inspector-total>09</span></p>
        <button type="button" aria-label="下一张照片" data-inspector-next>→</button>
      </div>
    </div>
  </dialog>
`;

export const createPage = (currentPage = "home") => `
  <div class="site-shell">
    <a class="skip-link" href="#main-content">跳到主要内容</a>
    ${preloader()}
    <div class="page-transition" aria-hidden="true">
      <span>${brand.englishName}</span>
    </div>
    <div class="page-noise" aria-hidden="true"></div>
    <div class="page-grid" aria-hidden="true"></div>
    <div class="ambient-glow" aria-hidden="true"></div>
    <div class="custom-cursor" aria-hidden="true" data-cursor></div>
    <div class="scroll-progress" aria-hidden="true">
      <div class="scroll-progress__bar"></div>
    </div>
    <div class="side-rail" aria-hidden="true">
      <span class="side-rail__label">SCROLL / EXPLORE</span>
      <i class="side-rail__line"></i>
    </div>
    ${header(currentPage)}
    <main class="site-main" id="main-content">
      ${renderPage(currentPage)}
    </main>
    ${footer()}
    ${mediaInspector()}
  </div>
`;
